/**
 * The Work list, "Whose work" pills and the Team → Review queue — ADR-001 §11.4, guide §5.2, §5.5.
 *
 * `GET /work` is ONE list of everything open, so it is a discriminated union on `kind`. Build step 2 fills
 * `task` and `approval` items; `client_request`, `internal_request`, `client_task` and `escalation` items
 * appear from step 3, `proposed` from step 8. Their shapes are defined now so the SPA renders one list.
 */
import { z } from 'zod';
import { IsoDate, PageQuery, Timestamp, Uuid, page } from './common.js';
import { ClientRef } from './clients.js';
import { UserRef } from './users.js';
import { ChangeRequest, Closure, HeadPriority, OriginRef, TaskPriority, TaskSource, WorkStatusFilter } from './tasks.js';
import { InternalRequestType } from './requests.js';
/** Chip tones = the prototype's chip classes. */
export const BadgeTone = z.enum(['cold', 'warm', 'hot', 'info', 'brand', 'neutral']).meta({ id: 'BadgeTone' });
/** The prototype's row chips (taskItem/slipChip). Keys are stable; `label` is the printed copy. */
export const BadgeKey = z
    .enum([
    'overdue', 'due_today', 'blocked', 'slipped', 'change_requested', 'client_waiting', 'proof_attached',
    'proof_checked', 'sent_back', 'high_priority_client', 'not_assigned', 'proposed', 'past_response_time',
])
    .meta({ id: 'BadgeKey' });
export const Badge = z
    .object({
    key: BadgeKey,
    /** e.g. "Blocked on the client", "Slipped 4d, 2 pushes", "Change requested". */
    label: z.string(),
    tone: BadgeTone,
    /** Hover text, e.g. "First due Mon 28 Sep". */
    title: z.string().optional(),
})
    .meta({ id: 'Badge' });
export const WorkKind = z
    .enum(['task', 'client_request', 'internal_request', 'client_task', 'approval', 'escalation', 'proposed'])
    .meta({ id: 'WorkKind' });
const WorkBase = z.object({
    id: Uuid,
    title: z.string(),
    client: ClientRef.nullable(),
    assignee: UserRef.nullable(),
    /** The date that decides overdue for this kind (task due, respond-by date, client task due…). */
    due: IsoDate.nullable(),
    priority: TaskPriority.nullable(),
    badges: z.array(Badge),
    /** SPA route that opens this item (side panel). */
    route: z.string().startsWith('/'),
    overdue: z.boolean(),
    /** Set when `group_by` was requested: the group this row belongs to. */
    group: z.object({ key: z.string(), label: z.string() }).nullable(),
});
export const WorkTask = WorkBase.extend({
    kind: z.literal('task'),
    done: z.boolean(),
    effective_head_priority: HeadPriority,
    slip_count: z.number().int().nonnegative(),
    check_state: Closure.shape.check_state.nullable(),
    /** Where it came from — the row's sub-line (TASK_SOURCE_LABEL: Voice note, Checklist, Ad hoc, …). */
    source: TaskSource,
    /** What it came from, when known (e.g. a meeting, a client request), with its label and route. */
    origin_ref: OriginRef.nullable(),
}).meta({ id: 'WorkTask' });
export const WorkApproval = WorkBase.extend({
    kind: z.literal('approval'),
    change_request: ChangeRequest,
}).meta({ id: 'WorkApproval' });
export const WorkClientRequest = WorkBase.extend({
    kind: z.literal('client_request'),
    via: z.enum(['Email', 'Call', 'WhatsApp', 'Meeting']),
    /** Negative once past the response time. */
    hours_left: z.number(),
    status: z.enum(['new', 'open', 'needs_details', 'done']),
    /** Set once answered (`status` done); the Done and All filters list these. */
    closed_at: Timestamp.nullable(),
}).meta({ id: 'WorkClientRequest' });
export const WorkInternalRequest = WorkBase.extend({
    kind: z.literal('internal_request'),
    team: z.enum(['sales', 'marketing']),
    status: z.enum(['waiting_triage', 'in_progress', 'needs_details', 'waiting_confirm', 'done', 'declined']),
    hours_to_ack: z.number().nullable(),
    /** Who asked (the Sales or Marketing person). */
    requester: UserRef,
    type: InternalRequestType,
    /** A prospect named instead of a client (`client` is then null). */
    prospect: z.string().nullable(),
}).meta({ id: 'WorkInternalRequest' });
export const WorkClientTask = WorkBase.extend({
    kind: z.literal('client_task'),
    promised_to: z.string().nullable(),
    status: z.enum(['open', 'kept', 'dropped']),
    /** Where it was said, e.g. "Meeting 28 Sep" (the row's sub-line). */
    source: z.string().nullable(),
    kept_at: Timestamp.nullable(),
}).meta({ id: 'WorkClientTask' });
export const WorkEscalation = WorkBase.extend({
    kind: z.literal('escalation'),
    severity: z.enum(['high', 'med', 'low']),
    opened_on: IsoDate,
    status: z.enum(['open', 'closed']),
    closed_on: IsoDate.nullable(),
}).meta({ id: 'WorkEscalation' });
export const WorkProposed = WorkBase.extend({
    kind: z.literal('proposed'),
    proposed_kind: z.enum(['task', 'esc']),
    /** The sentence it came from. */
    quote: z.string().nullable(),
    proposed_at: Timestamp,
}).meta({ id: 'WorkProposed' });
export const WorkItem = z
    .discriminatedUnion('kind', [WorkTask, WorkApproval, WorkClientRequest, WorkInternalRequest, WorkClientTask, WorkEscalation, WorkProposed])
    .meta({ id: 'WorkItem' });
export const WorkPage = page(WorkItem).meta({ id: 'WorkPage' });
/** `?type` values: the item kinds, plus `blocked` (tasks that are blocked). */
export const WorkTypeFilter = z
    .enum(['task', 'request', 'client_task', 'approval', 'escalation', 'blocked', 'proposed'])
    .meta({ id: 'WorkTypeFilter' });
const csv = (e) => z
    .string()
    .max(200)
    .transform((s, ctx) => {
    const parts = s.split(',').map((p) => p.trim()).filter(Boolean);
    const bad = parts.filter((p) => !e.safeParse(p).success);
    if (bad.length) {
        ctx.addIssue({ code: 'custom', message: `Unknown value(s): ${bad.join(', ')}. Use: ${e.options.join(', ')}` });
        return z.NEVER;
    }
    return parts;
});
/** Most people `?assignee_id` may list at once. */
export const MAX_ASSIGNEE_FILTER = 50;
/**
 * `?assignee_id`: a comma list of user ids, plus optionally `none` (= Not assigned). A single value is a list of
 * one, so `assignee_id=<uuid>` and `assignee_id=none` read exactly as before. Repeats are dropped; an empty
 * value is no filter (like `type`/`status`). Anything else, or more than 50 entries, is 422.
 */
export const AssigneeFilter = z
    .string()
    .max(4000)
    .transform((s, ctx) => {
    const parts = s.split(',').map((p) => p.trim()).filter(Boolean);
    if (parts.length > MAX_ASSIGNEE_FILTER) {
        ctx.addIssue({ code: 'custom', message: `At most ${MAX_ASSIGNEE_FILTER} people at once (got ${parts.length}).` });
        return z.NEVER;
    }
    const bad = parts.filter((p) => p !== 'none' && !Uuid.safeParse(p).success);
    if (bad.length) {
        const shown = bad.slice(0, 5).map((p) => (p.length > 40 ? `${p.slice(0, 40)}…` : p));
        ctx.addIssue({ code: 'custom', message: `Not a user id: ${shown.join(', ')}. Use user ids and/or none.` });
        return z.NEVER;
    }
    return [...new Set(parts)];
});
/**
 * GET /work. Managers see everyone's; an ops_member sees their own work and their own clients'.
 * `type` and `status` are comma lists (default: every type, status `open`). `assignee_id` is a comma list of user
 * ids and/or `none` (= Not assigned): an item matches when its assignee is any listed id, or it is unassigned and
 * `none` is listed. It is honoured for managers only, and only ever narrows what the caller may already see. Order: overdue first, then due date, then priority.
 * `status=done` (and `all`) also lists finished step-3 items: answered client requests, done or declined internal
 * requests, kept or dropped client tasks and closed escalations.
 */
export const ListWorkQuery = PageQuery.extend({
    type: csv(WorkTypeFilter).optional(),
    status: csv(WorkStatusFilter).optional(),
    assignee_id: AssigneeFilter.optional(),
    client_id: Uuid.optional(),
    group_by: z.enum(['client', 'who', 'due', 'type']).optional(),
    q: z.string().trim().min(1).max(80).optional(),
});
/** GET /work/whose — the managers' "Whose work" pill row: Everyone, each person, Not assigned. */
export const WhoseWorkQuery = z.object({ client_id: Uuid.optional() });
export const WhoseWorkRow = z
    .object({ user: UserRef, open: z.number().int().nonnegative(), overdue: z.number().int().nonnegative() })
    .meta({ id: 'WhoseWorkRow' });
export const WhoseWork = z
    .object({
    everyone: z.object({ open: z.number().int().nonnegative(), overdue: z.number().int().nonnegative() }),
    /** One row per active person with open work, most open first. */
    data: z.array(WhoseWorkRow),
    unassigned: z.object({ open: z.number().int().nonnegative(), overdue: z.number().int().nonnegative() }),
})
    .meta({ id: 'WhoseWork' });
// ---------------------------------------------------------------- review queue
export const ReviewKind = z
    .enum(['proof', 'request_proof', 'escalation_proof', 'change', 'held', 'proposed', 'incident'])
    .meta({ id: 'ReviewKind' });
const ReviewBase = z.object({
    id: z.string(),
    title: z.string(),
    client: ClientRef.nullable(),
    by: UserRef.nullable(),
    at: Timestamp,
    route: z.string().startsWith('/'),
    /** Hours waiting; held/proposed items are flagged after 4 hours. */
    waiting_hours: z.number().nonnegative(),
});
export const ReviewProof = ReviewBase.extend({
    kind: z.literal('proof'),
    task_id: Uuid,
    closure: Closure,
    /** Whether the CALLER may check this one (never their own; the AD's closures go to the founder). */
    can_check: z.boolean(),
}).meta({ id: 'ReviewProof' });
export const ReviewChange = ReviewBase.extend({
    kind: z.literal('change'),
    change_request: ChangeRequest,
    can_decide: z.boolean(),
}).meta({ id: 'ReviewChange' });
/** Proof on a closed client request or escalation (step 3), checked like a task's (decision 12). */
export const ReviewItemProof = ReviewBase.extend({
    kind: z.enum(['request_proof', 'escalation_proof']),
    item_id: Uuid,
    closure: Closure,
    can_check: z.boolean(),
}).meta({ id: 'ReviewItemProof' });
/**
 * An open held capture item (step 9, GET /held-items): `title` is its text clipped (or "Voice note — could not be
 * transcribed" when there is none), `by` who captured it, `detail` why it was held. Late after
 * CAPTURE_HELD_LATE_HOURS (waiting_hours).
 */
export const ReviewHeld = ReviewBase.extend({
    kind: z.literal('held'),
    /** The held item's id (POST /held-items/{id}/confirm or /discard). */
    ref_id: Uuid,
    detail: z.string().nullable(),
    note_id: Uuid,
}).meta({ id: 'ReviewHeld' });
/** Email proposals (step 8) and unclear incidents (step 4) — shape fixed now. */
export const ReviewOther = ReviewBase.extend({
    kind: z.enum(['proposed', 'incident']),
    ref_id: z.string(),
    detail: z.string().nullable(),
}).meta({ id: 'ReviewOther' });
export const ReviewItem = z
    .union([ReviewProof, ReviewItemProof, ReviewChange, ReviewHeld, ReviewOther])
    .meta({ id: 'ReviewItem' });
export const ListReviewQuery = PageQuery.extend({ kind: csv(ReviewKind).optional() });
export const ReviewPage = page(ReviewItem).meta({ id: 'ReviewPage' });
//# sourceMappingURL=work.js.map